import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CardActionArea, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BookIcon from '@mui/icons-material/Book';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HistoryIcon from '@mui/icons-material/History';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

const hubSections = [
  {
    title: 'Dictionary',
    description: 'At least 1000 academic words with comprehensive definitions and usage examples.',
    path: '/language-hub/dictionary',
    icon: <BookIcon sx={{ fontSize: 40, color: '#6366f1' }} />,
    bg: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.03) 100%)',
    borderColor: '#6366f1'
  },
  {
    title: 'Idioms',
    description: 'Master English expressions and figures of speech with natural context examples.',
    path: '/language-hub/idioms',
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: '#ec4899' }} />,
    bg: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.03) 100%)',
    borderColor: '#ec4899'
  },
  {
    title: 'Vocabulary Games',
    description: 'Challenge your brain with Word Match, Word Scramble, and Hangman games.',
    path: '/language-hub/games',
    icon: <SportsEsportsIcon sx={{ fontSize: 40, color: '#10b981' }} />,
    bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.03) 100%)',
    borderColor: '#10b981'
  },
  {
    title: 'Grammar Lab',
    description: 'Interactive syntax and punctuation guides to perfect your structure and mechanics.',
    path: '/language-hub/grammar',
    icon: <SpellcheckIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
    bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.03) 100%)',
    borderColor: '#f59e0b'
  },
  {
    title: 'Writing Studio',
    description: 'Essay structures, standard academic templates, formatting rules, and style manuals.',
    path: '/language-hub/writing',
    icon: <BorderColorIcon sx={{ fontSize: 40, color: '#3b82f6' }} />,
    bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.03) 100%)',
    borderColor: '#3b82f6'
  },
  {
    title: 'Reading Lounge',
    description: 'Comprehension passages paired with graded multiple-choice tests to evaluate skills.',
    path: '/language-hub/reading',
    icon: <MenuBookIcon sx={{ fontSize: 40, color: '#8b5cf6' }} />,
    bg: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.03) 100%)',
    borderColor: '#8b5cf6'
  },
  {
    title: 'Etymology Index',
    description: 'A deep dive into word origins, histories, prefixes, and semantic development.',
    path: '/language-hub/etymology',
    icon: <HistoryIcon sx={{ fontSize: 40, color: '#06b6d4' }} />,
    bg: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(6, 182, 212, 0.03) 100%)',
    borderColor: '#06b6d4'
  },
  {
    title: 'Syllabification',
    description: 'Explore syllable division rules and sound beats. Break down any word.',
    path: '/language-hub/syllabification',
    icon: <FormatSizeIcon sx={{ fontSize: 40, color: '#f43f5e' }} />,
    bg: 'linear-gradient(135deg, rgba(244, 63, 94, 0.1) 0%, rgba(244, 63, 94, 0.03) 100%)',
    borderColor: '#f43f5e'
  },
  {
    title: 'Daily Challenge',
    description: 'Test your vocabulary daily with Word of the Day mini-quizzes and speed tasks.',
    path: '/language-hub/daily-challenge',
    icon: <StarOutlineIcon sx={{ fontSize: 40, color: '#eab308' }} />,
    bg: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(234, 179, 8, 0.03) 100%)',
    borderColor: '#eab308'
  }
];

const LanguageHub: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Page Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 800, 
            background: 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)', 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Language Hub
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontWeight: 400 }}>
          Elevate your English skills. Explore our comprehensive suite of vocabulary builders, interactive syntax labs, games, and writing aids.
        </Typography>
      </Box>

      {/* Grid of Sections */}
      <Grid container spacing={4}>
        {hubSections.map((section, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card 
              sx={{ 
                height: '100%', 
                background: section.bg, 
                borderRadius: 4, 
                border: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 12px 30px -10px ${section.borderColor}40`,
                  borderColor: section.borderColor
                }
              }}
            >
              <CardActionArea 
                onClick={() => navigate(section.path)}
                sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}
              >
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 3, 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 3
                  }}
                >
                  {section.icon}
                </Box>
                <CardContent sx={{ p: 0, width: '100%' }}>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {section.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default LanguageHub;
